import React, {useState} from 'react'
import {Button, Input} from 'react-daisyui'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import clsx from 'clsx'
import {useForm} from 'react-hook-form'
import {signUp} from 'aws-amplify/auth'
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons'
import {
  trackFormSubmission,
  trackValidationError
} from '../../services/errorAnalytics'

export const CreateMemberForm = ({
  onSignUpSuccess,
  onUserExists
}: {
  onSignUpSuccess?: Function
  onUserExists?: Function
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    watch
  } = useForm()

  const [isSigningUp, setIsSigningUp] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Track validation errors
  const trackFieldValidationError = (
    fieldName: string,
    errorMessage: string
  ) => {
    trackValidationError('create_account', fieldName, errorMessage)
  }

  const handleSignUp = async (data: any) => {
    // event.preventDefault()
    setIsSigningUp(true)
    const caseInsensitiveEmail = data.email.toLowerCase()
    try {
      const {nextStep} = await signUp({
        username: caseInsensitiveEmail,
        password: data.password,
        options: {
          userAttributes: {
            given_name: data.given_name,
            phone_number: data.phone_number,
            family_name: data.family_name
          },
          autoSignIn: true
        }
      })

      // Track successful form submission
      trackFormSubmission('create_account', true)

      if (onSignUpSuccess) {
        onSignUpSuccess({nextStep, data})
      }
    } catch (error) {
      console.log('sign up error', error, Object.keys(error))

      const errorMessage =
        (error as {message?: string})?.message ||
        'An error occurred during sign up.'

      // Track failed form submission
      trackFormSubmission('create_account', false, errorMessage)

      // Amplify errors have a 'name' property for error type
      if ((error as {name?: string})?.name === 'UsernameExistsException') {
        if (onUserExists) {
          onUserExists(caseInsensitiveEmail)
        }
      }
      setErrorMessage(errorMessage)
    } finally {
      setIsSigningUp(false)
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  return (
    <div>
      {errorMessage && (
        <div style={{color: 'red'}} role="alert" aria-live="assertive">
          {errorMessage}
        </div>
      )}
      <form
        onSubmit={handleSubmit(handleSignUp)}
        className="flex flex-col gap-2 w-full"
        aria-label="Create Member Form"
      >
        <div>
          <label
            htmlFor="email"
            className={'label pb-1' + (errors.email ? ' text-red-error' : '')}
          >
            Email
          </label>
          <Input
            {...register('email', {required: true, pattern: /^\S+@\S+$/i})}
            type="email"
            id="email"
            name="email"
            className={'input-form' + (errors.email ? ' input-form-error' : '')}
            required
            aria-invalid={!!errors.email}
            aria-describedby="email-error"
            onBlur={e => {
              if (errors.email?.type === 'required') {
                trackFieldValidationError('email', 'Email is required')
              } else if (errors.email?.type === 'pattern') {
                trackFieldValidationError('email', 'Invalid email address')
              }
            }}
          />
          {errors.email?.type === 'required' && (
            <p id="email-error" className="text-red-500" role="alert">
              Email is required
            </p>
          )}
          {errors.email?.type === 'pattern' && (
            <p id="email-error" className="text-red-500" role="alert">
              Invalid email address
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="phone_number"
            className={
              'label pb-1' + (errors.phone_number ? ' text-red-error' : '')
            }
          >
            Phone Number:
          </label>
          <PhoneInputWithCountry
            placeholder="Enter phone number"
            defaultCountry="US"
            name="phone_number"
            id="phone_number"
            control={control}
            className={
              'PhoneInput' + (errors.phone_number ? ' input-form-error' : '')
            }
            aria-invalid={!!errors.phone_number}
            aria-describedby="phone-error"
            onBlur={e => {
              if (errors.phone_number?.type === 'required') {
                trackFieldValidationError(
                  'phone_number',
                  'Phone number is required'
                )
              } else if (errors.phone_number?.type === 'pattern') {
                trackFieldValidationError(
                  'phone_number',
                  'Invalid phone number'
                )
              }
            }}
          />
          {errors.phone_number?.type === 'required' && (
            <p id="phone-error" className="text-red-500" role="alert">
              Phone number is required
            </p>
          )}
          {errors.phone_number?.type === 'pattern' && (
            <p id="phone-error" className="text-red-500" role="alert">
              Invalid phone number
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="given_name"
            className={
              'label pb-1' + (errors.given_name ? ' text-red-error' : '')
            }
          >
            First Name:
          </label>
          <Input
            {...register('given_name', {
              required: true,
              pattern: /^[A-Za-z]+$/i
            })}
            id="given_name"
            className={
              'input-form' + (errors.given_name ? ' input-form-error' : '')
            }
            type="text"
            name="given_name"
            aria-invalid={!!errors.given_name}
            aria-describedby="given-name-error"
            onBlur={e => {
              if (errors.given_name?.type === 'required') {
                trackFieldValidationError(
                  'given_name',
                  'First name is required'
                )
              } else if (errors.given_name?.type === 'pattern') {
                trackFieldValidationError('given_name', 'Invalid first name')
              }
            }}
          />
          {errors.given_name?.type === 'required' && (
            <p id="given-name-error" className="text-red-500" role="alert">
              First name is required
            </p>
          )}
          {errors.given_name?.type === 'pattern' && (
            <p id="given-name-error" className="text-red-500" role="alert">
              Invalid first name
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="family_name"
            className={
              'label pb-1' + (errors.family_name ? ' text-red-error' : '')
            }
          >
            Last Name:
          </label>
          <Input
            {...register('family_name', {
              required: true,
              pattern: /^[A-Za-z]+$/i
            })}
            id="family_name"
            className={
              'input-form' + (errors.family_name ? ' input-form-error' : '')
            }
            type="text"
            name="family_name"
            aria-invalid={!!errors.family_name}
            aria-describedby="family-name-error"
            onBlur={e => {
              if (errors.family_name?.type === 'required') {
                trackFieldValidationError(
                  'family_name',
                  'Last name is required'
                )
              } else if (errors.family_name?.type === 'pattern') {
                trackFieldValidationError('family_name', 'Invalid last name')
              }
            }}
          />
          {errors.family_name?.type === 'required' && (
            <p id="family-name-error" className="text-red-500" role="alert">
              Last name is required
            </p>
          )}
          {errors.family_name?.type === 'pattern' && (
            <p id="family-name-error" className="text-red-500" role="alert">
              Invalid last name
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="password"
            className={
              'label pb-1' + (errors.password ? ' text-red-error' : '')
            }
          >
            Password:
          </label>
          <div className="relative">
            <Input
              {...register('password', {required: true})}
              id="password"
              className={
                'input-form' + (errors.password ? ' input-form-error' : '')
              }
              type={showPassword ? 'text' : 'password'}
              name="password"
              aria-invalid={!!errors.password}
              aria-describedby="password-error"
              onBlur={e => {
                if (errors.password?.type === 'required') {
                  trackFieldValidationError('password', 'Password is required')
                }
              }}
            />
            <span
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <FontAwesomeIcon className="text-gray-400" icon={faEye} />
              ) : (
                <FontAwesomeIcon className="text-gray-400" icon={faEyeSlash} />
              )}
            </span>
          </div>
          {errors.password?.type === 'required' && (
            <p id="password-error" className="text-red-500" role="alert">
              Password is required
            </p>
          )}
        </div>
        <label className="form-control">
          <div className="label justify-start">
            <input
              type="checkbox"
              className="checkbox mr-2"
              {...register('acceptTerms', {required: true})}
              aria-invalid={!!errors.acceptTerms}
              aria-describedby="terms-error"
            />
            <span className="label-text text-left">
              I accept Placemakr's{' '}
              <a
                className="link text-cabana text-bold no-underline hover:underline"
                href="https://www.placemakr.com/terms-and-conditions"
                target="_blank"
                onClick={e => {
                  e.stopPropagation()
                }}
                aria-label="View Terms and Conditions."
              >
                Terms and Conditions
              </a>
              .
            </span>
          </div>
          {errors.acceptTerms ? (
            <div className="label">
              <span
                id="terms-error"
                className="label-text-alt text-red-500"
                role="alert"
              >
                Terms and Conditions must be accepted.
              </span>
            </div>
          ) : (
            ''
          )}
        </label>
        <p className="text-left text-xs text-gray-500">
          By submitting your mobile phone number and checking the box above, you
          consent to receive text messages from Placemakr with important
          information about your stay. Message frequency may vary and data rates
          may apply. Reply STOP to opt out at any time.
        </p>
        <Button
          color="secondary"
          className="w-full mt-4"
          type="submit"
          aria-busy={isSigningUp}
        >
          create account
        </Button>
        <div className={clsx({hidden: !isSigningUp})}>
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      </form>
    </div>
  )
}

export default CreateMemberForm
